<template>
  <input
    type="text"
    name="searchTerm"
    v-model="searchTerm"
    ref="searchTermInput"
    class="rounded-md focus:ring-0 focus:border-gray-300 w-full mb-1 text-sm"
    placeholder="Suche.."
    @keydown.escape="emit('stopEditing')"
    @keydown.down="handleKeyDown"
    @keydown.up="handleKeyUp"
    @keydown.enter="handleEnter"
    autocomplete="off"
    autocorrect="off"
  />
  <div
    class="bg-slate-100 hover:bg-slate-200 cursor-pointer p-2 m-0 border-b border-gray-200 text-left text-sm"
    :class="{ 'bg-blue-100': selectedIndex === -1 }"
    @click="emit('addVariable')"
  >
    + Neue Variable
  </div>
  <div class="overflow-y-auto max-h-[200px] list-none m-0 p-0 rounded-md bg-white shadow-md">
    <div
      v-for="(variable, index) in filteredVariables"
      :key="variable.code"
      class="hover:bg-gray-100 p-2 m-0 border-b border-gray-200 cursor-pointer text-left text-sm"
      :class="{ 'bg-blue-100': selectedIndex === index }"
      @click="emit('setVariable', variable.code)"
    >
      {{ variable.label }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TemplateVariable } from '@/questionnaire/types'
import type { Editor } from '@tiptap/core'
import { computed, onMounted, ref } from 'vue'

interface Props {
  variables: TemplateVariable[]
  editor: Editor
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'addVariable'): void
  (e: 'stopEditing'): void
  (e: 'setVariable', code: string): void
}>()

const searchTerm = ref('')
const searchTermInput = ref<HTMLInputElement | null>(null)
const selectedIndex = ref(-1) // -1 means "New Variable" is selected

const filteredVariables = computed(() => {
  return props.variables.filter((variable: TemplateVariable) => variable.label.toLowerCase().includes(searchTerm.value.toLowerCase()))
})

const handleKeyDown = () => {
  console.log('keydown')
  if (selectedIndex.value < filteredVariables.value.length - 1) {
    selectedIndex.value++
  } else {
    selectedIndex.value = -1 // Wrap to "New Variable"
  }
}

const handleKeyUp = () => {
  console.log('keyup')
  if (selectedIndex.value > -1) {
    selectedIndex.value--
  } else {
    selectedIndex.value = filteredVariables.value.length - 1 // Wrap to last variable
  }
}

const handleEnter = () => {
  if (selectedIndex.value === -1) {
    handleAddVariable()
  } else {
    const selectedVariable = filteredVariables.value[selectedIndex.value]
    handleSetVariable(selectedVariable.code)
  }
}

const handleSetVariable = (code: string) => {
  // Check if the editor has the replaceWithTemplateVariable command
  if (props.editor.commands.replaceWithTemplateVariable) {
    // Use the extension command to handle the replacement
    props.editor.commands.replaceWithTemplateVariable(code)
  } else {
    // Fallback to emitting the event for parent to handle
    emit('setVariable', code)
  }
  emit('stopEditing')
}

const handleAddVariable = () => {
  emit('addVariable')
}

onMounted(() => {
  searchTermInput.value?.focus()
})
</script>
